import * as yup from "yup"
import { yupResolver } from "@hookform/resolvers/yup"
import { Typography } from "@stardust-ds/react"
import { useEffect, useState } from "react"
import intl from "react-intl-universal"

import Modal from "../../molecules/Modal"
import ColorPicker from "../../molecules/ColorPicker"
import { useForm } from "react-hook-form"
import Input from "../../atoms/Input"
import styles from "./index.module.scss"
import Button from "../../atoms/Button"
import Label from "../../atoms/Label"
import RcmpSelectUsers from "../../molecules/SelectUsers"
import useCheckSubmitRegister from "../../../hooks/useCheckSubmitRegister"

const C_DEFAULT_COLOR = "#000000"

export default function ModalAddPainel(props) {
  const {
    isOpen,
    onClose,
    createCard,
    isEdit,
    onCancel,
    handleEditPainel,
    oPainelData,
    oPanelRes
  } = props

  const [cColor, fnSetCColor] = useState(C_DEFAULT_COLOR)

  const [fnCheckRegister, bLoadingRegister] = useCheckSubmitRegister()

  const schema = yup
    .object()
    .shape({
      name: yup.string().required(intl.get("form.required_field")),
      description: yup.string().required(intl.get("form.required_field")),
      responsibles: yup
        .array()
        .min(1, intl.get("form.select_responsibles"))
        .of(yup.number())
        .required(intl.get("form.select_responsibles")),
      color: yup.string().default(cColor || "#6CB6E3"),
    })

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
  })

  function fnResetForm() {
    reset({
      name: "",
      description: "",
      responsibles: [],
      color: C_DEFAULT_COLOR,
    })
    fnSetCColor(C_DEFAULT_COLOR)
  }

  function fnCloseModal() {
    fnResetForm()
    onClose()
  }

  async function fnCheckPanel() {
    const oCheckRegister = {
      nPanelId: oPainelData?.id
    }

    return await fnCheckRegister(oCheckRegister, intl.get("DashboardPage.update_panel_error"))
  }

  const onSubmit = async (data) => {
    if (!isEdit) {
      await createCard(data)
      fnCloseModal()
      return
    }

    const bCanSubmit = await fnCheckPanel()
    if (!bCanSubmit) {
      fnCloseModal()
      return
    }

    await handleEditPainel(data)
    fnCloseModal()
  }

  useEffect(() => {
    if (!isEdit || !oPainelData) return

    fnSetCColor(oPainelData.color)
    const aResponsibles = oPainelData?.responsibles?.map(r => r?.user_nid) || []
    reset({
      ...oPainelData,
      responsibles: aResponsibles
    })
  }, [oPainelData?.id, isEdit])

  const aResponsibles = oPainelData?.responsibles?.length > 0 ? oPainelData.responsibles.map((r) => ({
    value: r?.user_nid,
    label: r?.user_cname,
  })) : []

  useEffect(() => {
    if (!isOpen) fnResetForm()
  }, [isOpen])

  const cModalTitle = isEdit ? intl.get("DashboardPage.edit_panel_name", { panel: oPainelData?.name }) : intl.get("DashboardPage.add_panel")
  const bSubmitLoading = oPanelRes.loading || bLoadingRegister

  return (
    <Modal isOpen={isOpen} size="lg">
      <Modal.Header onClose={onCancel}>
        {cModalTitle}
      </Modal.Header>
      <form onSubmit={handleSubmit(onSubmit)} className={styles.modalContent}>
        <Modal.Content size="lg">
          <div className={styles.modalContainer}>
            <div className={styles.row}>
              <Input
                required
                isFullWidth
                label={`${intl.get("DashboardPage.panel_name")} *`}
                placeholder={intl.get("DashboardPage.panel_name_placeholder")}
                type="text"
                {...register("name")}
              />
              <Typography
                color="#FF3541"
                type="p3"
                className={styles.errorMessage}
              >
                {errors.name?.message}
              </Typography>
            </div>
            <div className={styles.rowDescription}>
              <Label>{intl.get("DashboardPage.description")} *</Label>
              <textarea
                required
                className={styles.description}
                placeholder={intl.get("DashboardPage.description_placeholder")}
                type="textarea"
                {...register("description")}
              />
              <Typography
                color="#FF3541"
                type="p3"
                className={styles.errorMessage}
              >
                {errors.description?.message}
              </Typography>
            </div>
            <div className={styles.row}>
              <RcmpSelectUsers
                required
                multiSelect
                placeholder={`${intl.get("select_to_add")}...`}
                defaultValue={aResponsibles}
                label={`${intl.get("DashboardPage.responsibles")} *`}
                isFullWidth
                searchable
                onSelect={(e) =>
                  setValue(
                    "responsibles",
                    e?.map((v) => v?.value)
                  )
                }
              />
              <Typography
                color="#FF3541"
                type="p3"
                className={styles.errorMessage}
              >
                {errors.responsibles?.message}
              </Typography>
            </div>
            <div className={styles.rowColor}>
              <ColorPicker
                name="colorPicker"
                label={`${intl.get("DashboardPage.panel_color")} *`}
                isFullWidth
                onChange={(e) => setValue("color", e)}
                value={cColor}
              />
            </div>
          </div>
        </Modal.Content>
        <Modal.Footer>
          <Button type="button" variant="outline" onClick={onCancel} disabled={bSubmitLoading}>
            {intl.get("cancel")}
          </Button>
          <Button type="submit" isLoading={bSubmitLoading}>
            {intl.get("save")}
          </Button>
        </Modal.Footer>
      </form>
    </Modal>
  )
}
